import { useState, useEffect, useCallback } from 'react';
import { X, Copy, Check, FileText, Code } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import { StatusBadge } from '../common/StatusBadge';
import { Spinner } from '../common/Spinner';
import { useJobStore } from '../../stores/jobStore';
import type { JobRun } from '../../types/jobs';
import { formatDuration } from './JobCard';
import { format, parseISO } from 'date-fns';

const mdComponents: Components = {
  h1: ({ children }) => (
    <h1 className="text-xl font-bold text-text-primary mt-6 mb-3">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-lg font-semibold text-text-primary mt-5 mb-2">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-base font-medium text-text-primary mt-4 mb-2">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-medium text-text-primary mt-3 mb-1">{children}</h4>
  ),
  p: ({ children }) => (
    <p className="text-sm text-text-secondary mb-3 leading-relaxed">{children}</p>
  ),
  a: ({ href, children }) => (
    <a href={href} className="text-accent-purple-light hover:underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  ul: ({ children }) => (
    <ul className="text-sm text-text-secondary list-disc pl-6 mb-3 space-y-1">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="text-sm text-text-secondary list-decimal pl-6 mb-3 space-y-1">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="leading-relaxed">{children}</li>
  ),
  code: ({ className, children }) => {
    const isBlock = className?.includes('language-');
    if (isBlock) {
      return <code className="text-sm font-mono">{children}</code>;
    }
    return (
      <code className="bg-bg-tertiary text-accent-purple-light px-1.5 py-0.5 rounded text-xs font-mono">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="bg-bg-tertiary border border-border-subtle rounded-lg p-4 overflow-x-auto mb-4 text-sm">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-accent-purple pl-4 italic text-text-muted mb-3">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto mb-4">
      <table className="w-full text-sm border border-border-subtle">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-bg-tertiary">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="px-3 py-2 text-left text-xs font-semibold text-text-primary border border-border-subtle">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2 text-text-secondary border border-border-subtle">{children}</td>
  ),
  hr: () => (
    <hr className="border-border-subtle my-6" />
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-text-primary">{children}</strong>
  ),
};

interface ArtifactViewerProps {
  run: JobRun;
  jobId: string;
  onClose: () => void;
}

export default function ArtifactViewer({ run, jobId, onClose }: ArtifactViewerProps) {
  const artifact = useJobStore((s) => s.artifact);
  const artifactLoading = useJobStore((s) => s.artifactLoading);
  const fetchArtifact = useJobStore((s) => s.fetchArtifact);
  const clearArtifact = useJobStore((s) => s.clearArtifact);

  const [viewMode, setViewMode] = useState<'rendered' | 'raw'>('rendered');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchArtifact(jobId, run.id);
    return () => clearArtifact();
  }, [jobId, run.id, fetchArtifact, clearArtifact]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  async function handleCopy() {
    if (!artifact) return;
    await navigator.clipboard.writeText(artifact);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-bg-secondary max-w-4xl w-full max-h-[90vh] overflow-hidden rounded-xl border border-border-subtle flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <h2 className="text-lg font-semibold text-text-primary truncate">Job Output</h2>
            <StatusBadge status={run.status} size="sm" />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* View mode toggle */}
            <div className="flex bg-bg-tertiary rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('rendered')}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  viewMode === 'rendered'
                    ? 'bg-accent-purple text-white'
                    : 'text-text-muted hover:text-text-secondary'
                }`}
              >
                <FileText className="h-3.5 w-3.5" />
                Rendered
              </button>
              <button
                onClick={() => setViewMode('raw')}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  viewMode === 'raw'
                    ? 'bg-accent-purple text-white'
                    : 'text-text-muted hover:text-text-secondary'
                }`}
              >
                <Code className="h-3.5 w-3.5" />
                Raw
              </button>
            </div>

            {/* Copy button */}
            <button
              onClick={handleCopy}
              disabled={!artifact}
              className="p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-bg-tertiary transition-colors disabled:opacity-50"
              title="Copy output"
            >
              {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
            </button>

            {/* Close */}
            <button
              onClick={onClose}
              className="text-text-muted hover:text-text-primary transition-colors p-1 rounded"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Run metadata */}
        <div className="px-6 py-3 border-b border-border-subtle flex items-center gap-4 text-xs text-text-muted shrink-0">
          <span className="font-mono">{jobId}</span>
          <span>Run #{run.id}</span>
          <span>{format(parseISO(run.started_at), 'MMM d, yyyy h:mm a')}</span>
          {run.duration_ms != null && <span>{formatDuration(run.duration_ms)}</span>}
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {artifactLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" />
            </div>
          ) : !artifact ? (
            <div className="text-center py-16">
              <p className="text-text-muted text-sm">No output available for this run.</p>
            </div>
          ) : viewMode === 'rendered' ? (
            <div className="max-w-3xl">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {artifact}
              </ReactMarkdown>
            </div>
          ) : (
            <pre className="text-sm text-text-secondary font-mono whitespace-pre-wrap break-words leading-relaxed">
              {artifact}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
